import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';

const StudentJoinGame = ({ studentEmail, studentName, onBack }) => {
  const [gameId, setGameId] = useState('');
  const [carSelected, setCarSelected] = useState('🚗');
  const [gameState, setGameState] = useState(null);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [ws, setWs] = useState(null);
  const [joined, setJoined] = useState(false);

  const cars = ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐'];

  const handleJoinGame = async () => {
    if (!gameId.trim()) {
      setError('Please enter a Game ID');
      return;
    }

    setJoining(true);
    setError('');

    try {
      // Get current game state
      const stateResponse = await fetch(buildApiUrl(`/api/racing-game/${gameId}/state`));
      if (!stateResponse.ok) {
        throw new Error('Game not found');
      }
      const gameData = await stateResponse.json();

      // Join the game
      const joinResponse = await fetch(buildApiUrl('/api/racing-game/join'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_id: gameId,
          student_id: studentEmail,
          full_name: studentName,
          car_emoji: carSelected
        })
      });

      if (!joinResponse.ok) {
        const errData = await joinResponse.json();
        throw new Error(errData.detail || 'Failed to join game');
      }

      setGameState(gameData);
      setJoined(true);
      connectWebSocket(gameId);
    } catch (err) {
      setError(err.message);
    } finally {
      setJoining(false);
    }
  };

  const connectWebSocket = (gId) => {
    const wsUrl = buildApiUrl(`/ws/racing-game/${gId}`).replace('http', 'ws');
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('🎮 Connected to game:', gId);
      websocket.send(JSON.stringify({ type: 'request_update' }));
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 WebSocket message:', data.type);
      if (data.type === 'game_state' || data.type === 'game_update' || data.type === 'game_started') {
        setGameState(data.data.game_state || data.data);
      }
    };

    websocket.onerror = () => {
      console.error('WebSocket error');
    };

    setWs(websocket);
  };

  const handleSubmitAnswer = async (questionId, answer) => {
    try {
      const response = await fetch(buildApiUrl('/api/racing-game/submit-answer'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_id: gameId,
          student_id: studentEmail,
          question_id: questionId,
          answer: answer
        })
      });

      const result = await response.json();
      
      if (result.success) {
        // Update game state with new progress
        if (ws) {
          ws.send(JSON.stringify({ type: 'request_update' }));
        }
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
    }
  };

  // Racing View (after joining)
  if (joined && gameState) {
    // Find current player in game state
    const currentPlayer = gameState.players?.find(p => p.student_id === studentEmail);
    const isCurrentPlayerFinished = currentPlayer?.finished;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => {
                setJoined(false);
                setGameId('');
                if (ws) ws.close();
              }}
              className="mb-4 text-amber-700 hover:text-amber-900 font-semibold flex items-center gap-2"
            >
              ← Back to Join Game
            </button>
            <h1 className="text-3xl font-bold text-gray-900">🏎️ Racing Game</h1>
            <p className="text-gray-600">Game ID: {gameState.game_id}</p>
          </div>

          {/* Game Status */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Status: <span className={
                gameState.status === 'waiting' ? 'text-yellow-600' :
                gameState.status === 'active' ? 'text-green-600' :
                'text-gray-600'
              }>{gameState.status || 'waiting'}</span>
            </h2>
          </div>

          {/* Waiting for Game to Start */}
          {gameState.status === 'waiting' && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="text-6xl mb-4">{carSelected}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Waiting for Game to Start</h3>
              <p className="text-gray-600 mb-6">
                Players joined: {gameState.players?.length || 0}
              </p>
              <div className="inline-block animate-spin text-4xl">⏳</div>
            </div>
          )}

          {/* Active Game - Questions & Racing Track */}
          {gameState.status === 'active' && !isCurrentPlayerFinished && (
            <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
              <h3 className="text-xl font-bold text-gray-900">🏁 Race In Progress</h3>
              
              {/* Current Question Section */}
              {gameState.questions && gameState.questions.length > 0 && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border-2 border-amber-300 p-6">
                  <div className="mb-4">
                    <span className="text-sm font-semibold text-amber-700">Question {(gameState.current_question_index || 0) + 1} of {gameState.total_questions || gameState.questions.length}</span>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-6">
                    {gameState.questions[gameState.current_question_index || 0]?.question || 'Loading...'}
                  </h4>
                  
                  {/* Answer Options */}
                  {gameState.questions[gameState.current_question_index || 0]?.options && (
                    <div className="grid grid-cols-2 gap-3">
                      {gameState.questions[gameState.current_question_index || 0].options.map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSubmitAnswer(gameState.questions[gameState.current_question_index || 0].id, option)}
                          className="p-4 bg-white border-2 border-amber-300 rounded-lg hover:bg-amber-100 hover:border-amber-500 transition-all font-semibold text-gray-900 text-lg"
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Race Track */}
              <div className="relative bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-6 overflow-hidden">
                <div className="relative h-48 bg-gray-50 rounded-lg border-4 border-gray-300 overflow-y-auto">
                  {/* Progress Bars for Each Player */}
                  {gameState.players?.map((player) => (
                    <div key={player.student_id} className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-900 text-sm">
                          {player.car_emoji} {player.full_name}
                        </span>
                        <span className="text-xs text-gray-600">
                          {player.progress}/{gameState.total_questions || 10}
                        </span>
                      </div>
                      <div className="w-full bg-gray-300 rounded-full h-4 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-amber-500 to-yellow-600 h-full flex items-center justify-center text-xs font-bold text-white transition-all duration-300"
                          style={{ width: `${Math.min((player.progress / (gameState.total_questions || 10)) * 100, 100)}%` }}
                        >
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Finish Line */}
                  <div className="absolute right-0 top-0 bottom-0 w-1 bg-red-600 flex items-center">
                    <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 text-lg">🏁</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Game Finished - Only for this student */}
          {isCurrentPlayerFinished && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">🏆 You Finished!</h3>
              <p className="text-gray-600 mb-8">Great job! You answered all the questions.</p>
              
              {/* Leaderboard */}
              <div className="space-y-2 mb-8">
                <p className="font-semibold text-gray-700 mb-4">Current Standings</p>
                {gameState.players?.sort((a, b) => b.progress - a.progress).map((player, idx) => (
                  <div key={player.student_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold">
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '•'} {player.car_emoji} {player.full_name}
                    </span>
                    <span className="font-bold text-amber-600">
                      {player.progress}/{gameState.total_questions || 10} 
                      {player.finished && <span className="ml-2 text-green-600">✓</span>}
                    </span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  setJoined(false);
                  setGameId('');
                  if (ws) ws.close();
                }}
                className="mt-8 px-8 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-xl hover:from-amber-700 hover:to-yellow-700 transition-all shadow-lg font-semibold"
              >
                Back to Join Game
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Join Game Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="mb-4 text-amber-700 hover:text-amber-900 font-semibold flex items-center gap-2"
          >
            ← Back to Games
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">🏎️ Join Racing Game</h1>
          <p className="text-gray-600">Enter the Game ID from your teacher to join</p>
        </div>

        {/* Join Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          {/* Game ID Input */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Enter Game ID
            </label>
            <input
              type="text"
              value={gameId}
              onChange={(e) => setGameId(e.target.value.toUpperCase())}
              placeholder="e.g., GAME-12345"
              className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition text-lg font-mono"
            />
          </div>

          {/* Car Selection */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Choose Your Car
            </label>
            <div className="grid grid-cols-5 gap-3">
              {cars.map((car) => (
                <button
                  key={car}
                  onClick={() => setCarSelected(car)}
                  className={`aspect-square rounded-xl text-3xl flex items-center justify-center transition-all ${
                    carSelected === car
                      ? 'bg-amber-500 ring-4 ring-amber-300 scale-110'
                      : 'bg-gray-100 hover:bg-amber-50 border-2 border-gray-200'
                  }`}
                >
                  {car}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Car Display */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center">
            <p className="text-gray-600 mb-2">Your Car</p>
            <div className="text-6xl mb-2">{carSelected}</div>
            <p className="font-semibold text-gray-900">{studentName}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
              <p className="text-red-700 font-semibold">❌ {error}</p>
            </div>
          )}

          {/* Join Button */}
          <button
            onClick={handleJoinGame}
            disabled={joining || !gameId.trim()}
            className="w-full py-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-xl hover:from-amber-700 hover:to-yellow-700 transition-all shadow-lg font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {joining ? '🔄 Joining Game...' : '🚀 Join Game'}
          </button>

          {/* Info */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <p className="text-sm text-blue-900">
              <strong>💡 How it works:</strong> Get the Game ID from your teacher, enter it above, select a car, and click "Join Game" to start racing!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentJoinGame;
